// KontaktKonfliktDialog.tsx — „Jemand anderes hat diesen Kontakt inzwischen
// geändert" (28.09.2026). Erscheint, wenn das Sperren der Akte mit 409
// scheitert (apps/api/src/contactBearbeitungsstand.ts).
//
// Drei Wege:
//   „Meine Änderungen auf den neuen Stand anwenden" — lädt den neuen Stand,
//     rechnet den eigenen Diff darauf um (utils/kontaktKonflikt.ts). Echte
//     Feldkonflikte (beide haben dasselbe Feld verschieden geändert) listet
//     der Dialog und fragt je Feld; ohne Konflikte geht es direkt weiter.
//   „Verwerfen und neu laden" — eigene Eingaben weg, der neue Stand gilt.
//   „Abbrechen" — nichts passiert, das Schloss bleibt offen, die Eingaben
//     bleiben stehen.
//
// Wie der Ungespeichert-Dialog: kein Fluent, kein window.confirm, sondern die
// Popup-Schale der Akte `.kvp-rd.kvp-rd-scrim > .dm` per Portal.

import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { Z_LAYERS } from "../windowManager/zLayers";
import { konfliktWerWann, type FeldKonflikt, type KontaktKonfliktInfo } from "../../utils/kontaktKonflikt";
import "./contactRedesign.css";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

const knopf: React.CSSProperties = {
  appearance: "none",
  border: "1.5px solid #ddd3dc",
  background: "#fff",
  color: "#591149",
  cursor: "pointer",
  font: '700 13.5px/1.2 "Hanken Grotesk", sans-serif',
  padding: "10px 16px",
  borderRadius: 10,
};
const primaer: React.CSSProperties = { ...knopf, background: "#7d2169", borderColor: "#7d2169", color: "#fff" };
const leise: React.CSSProperties = { ...knopf, border: "1.5px solid transparent", background: "transparent", color: "#6b5a66" };
const text: React.CSSProperties = { margin: 0, font: '500 14.5px/1.5 "Hanken Grotesk", sans-serif', color: "#241522" };
const klein: React.CSSProperties = { font: '500 13px/1.4 "Hanken Grotesk", sans-serif', color: "#6b5a66" };

export type KonfliktErgebnis<T> =
  | { wahl: "abbrechen" }
  | { wahl: "verwerfen" }
  | { wahl: "anwenden"; vorbereitet: T; derenBehalten: Set<string> };

type Phase<T> =
  | { art: "zu" }
  | { art: "frage" }
  | { art: "laedt" }
  | { art: "fehler"; meldung: string }
  | { art: "konflikte"; vorbereitet: T; konflikte: FeldKonflikt[] };

/**
 * Die Rückfrage als Promise: `await klaeren(info, vorbereiten)`.
 * `vorbereiten` lädt den neuen Stand und rechnet die eigenen Änderungen
 * darauf um — erst, wenn der Anwender „anwenden" wählt.
 */
export function useKontaktKonflikt<T extends { konflikte: FeldKonflikt[] }>(): {
  klaeren: (info: KontaktKonfliktInfo | null, vorbereiten: () => Promise<T>) => Promise<KonfliktErgebnis<T>>;
  dialog: React.ReactNode;
} {
  const [phase, setPhase] = React.useState<Phase<T>>({ art: "zu" });
  const [info, setInfo] = React.useState<KontaktKonfliktInfo | null>(null);
  const [deren, setDeren] = React.useState<Set<string>>(new Set());
  const offenRef = React.useRef<{ resolve: (e: KonfliktErgebnis<T>) => void; vorbereiten: () => Promise<T> } | null>(
    null,
  );

  const beenden = React.useCallback((e: KonfliktErgebnis<T>) => {
    const o = offenRef.current;
    offenRef.current = null;
    setPhase({ art: "zu" });
    o?.resolve(e);
  }, []);

  const klaeren = React.useCallback((i: KontaktKonfliktInfo | null, vorbereiten: () => Promise<T>) => {
    offenRef.current?.resolve({ wahl: "abbrechen" });
    setInfo(i);
    setDeren(new Set());
    setPhase({ art: "frage" });
    return new Promise<KonfliktErgebnis<T>>((resolve) => {
      offenRef.current = { resolve, vorbereiten };
    });
  }, []);

  const anwenden = React.useCallback(async () => {
    const o = offenRef.current;
    if (!o) return;
    setPhase({ art: "laedt" });
    try {
      const vorbereitet = await o.vorbereiten();
      if (vorbereitet.konflikte.length === 0) {
        beenden({ wahl: "anwenden", vorbereitet, derenBehalten: new Set() });
        return;
      }
      setPhase({ art: "konflikte", vorbereitet, konflikte: vorbereitet.konflikte });
    } catch (e) {
      setPhase({
        art: "fehler",
        meldung: `Der neue Stand konnte nicht geladen werden (${e instanceof Error ? e.message : String(e)}).`,
      });
    }
  }, [beenden]);

  const dialog = (
    <KontaktKonfliktDialog
      phase={phase}
      info={info}
      deren={deren}
      setDeren={setDeren}
      onAnwenden={() => void anwenden()}
      onUebernehmen={() =>
        phase.art === "konflikte" && beenden({ wahl: "anwenden", vorbereitet: phase.vorbereitet, derenBehalten: deren })
      }
      onVerwerfen={() => beenden({ wahl: "verwerfen" })}
      onAbbrechen={() => beenden({ wahl: "abbrechen" })}
    />
  );
  return { klaeren, dialog };
}

function KontaktKonfliktDialog<T>({
  phase,
  info,
  deren,
  setDeren,
  onAnwenden,
  onUebernehmen,
  onVerwerfen,
  onAbbrechen,
}: {
  phase: Phase<T>;
  info: KontaktKonfliktInfo | null;
  deren: Set<string>;
  setDeren: (s: Set<string>) => void;
  onAnwenden: () => void;
  onUebernehmen: () => void;
  onVerwerfen: () => void;
  onAbbrechen: () => void;
}) {
  const offen = phase.art !== "zu";
  const laedt = phase.art === "laedt";
  const ersterRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    if (!offen) return;
    ersterRef.current?.focus();
    const taste = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      if (!laedt) onAbbrechen();
    };
    alleDokumente.addEventListener("keydown", taste, true);
    return () => alleDokumente.removeEventListener("keydown", taste, true);
  }, [offen, laedt, onAbbrechen, phase.art]);

  if (!offen) return null;
  const { wer, wann } = konfliktWerWann(info);
  const derenName = info?.geaendertVon?.name?.trim() || "neuer Stand";

  return createPortal(
    <div
      className="kvp-rd kvp-rd-scrim"
      style={{ zIndex: Z_LAYERS.windowConfirm }}
      onClick={() => !laedt && onAbbrechen()}
      data-testid="konflikt-scrim"
    >
      <div
        className="dm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="konflikt-titel"
        aria-describedby="konflikt-text"
        style={{ width: phase.art === "konflikte" ? "min(680px, 100%)" : "min(560px, 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dm-head">
          <div className="dm-head-top">
            <div className="dm-titles">
              <div className="dm-kicker">Kontaktakte</div>
              <h2 id="konflikt-titel">
                {phase.art === "konflikte" ? "Beide haben dieselben Felder geändert" : "Kontakt wurde inzwischen geändert"}
              </h2>
            </div>
          </div>
        </div>

        <div className="dm-body" style={{ paddingTop: 4, display: "grid", gap: 12 }}>
          {phase.art === "konflikte" ? (
            <>
              <p id="konflikt-text" style={text}>
                Welcher Wert soll gelten? Alle übrigen Änderungen — Ihre und die von {wer} — bleiben erhalten.
              </p>
              <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="konflikt-liste">
                <thead>
                  <tr style={{ ...klein, textAlign: "left" }}>
                    <th style={{ padding: "4px 6px", fontWeight: 700 }}>Feld</th>
                    <th style={{ padding: "4px 6px", fontWeight: 700 }}>Ihre Eingabe</th>
                    <th style={{ padding: "4px 6px", fontWeight: 700 }}>Von {derenName}</th>
                  </tr>
                </thead>
                <tbody>
                  {phase.konflikte.map((k) => {
                    const nimmtDeren = deren.has(k.feld);
                    const setze = (d: boolean) => {
                      const s = new Set(deren);
                      if (d) s.add(k.feld);
                      else s.delete(k.feld);
                      setDeren(s);
                    };
                    return (
                      <tr key={k.feld} style={{ borderTop: "1px solid #ece4ea" }}>
                        <td style={{ padding: "8px 6px", ...text, fontWeight: 700 }}>{k.label}</td>
                        <td style={{ padding: "8px 6px" }}>
                          <label style={{ ...text, display: "flex", gap: 6, alignItems: "baseline" }}>
                            <input
                              type="radio"
                              name={`konflikt-${k.feld}`}
                              checked={!nimmtDeren}
                              onChange={() => setze(false)}
                            />
                            <span>{k.meins}</span>
                          </label>
                        </td>
                        <td style={{ padding: "8px 6px" }}>
                          <label style={{ ...text, display: "flex", gap: 6, alignItems: "baseline" }}>
                            <input
                              type="radio"
                              name={`konflikt-${k.feld}`}
                              checked={nimmtDeren}
                              onChange={() => setze(true)}
                            />
                            <span>{k.deren}</span>
                          </label>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          ) : (
            <>
              <p id="konflikt-text" style={text}>
                {wer} hat diesen Kontakt inzwischen geändert{wann ? ` (${wann})` : ""}. Ihre Änderungen sind noch
                nicht gespeichert.
              </p>
              {phase.art === "fehler" ? (
                <p role="alert" style={{ ...text, color: "#a4262c" }}>
                  {phase.meldung}
                </p>
              ) : (
                <p style={klein}>
                  „Anwenden" übernimmt die neue Fassung und setzt Ihre Änderungen darauf. Haben Sie dasselbe Feld
                  geändert, werden Sie gefragt.
                </p>
              )}
            </>
          )}
        </div>

        <div className="dm-foot" style={{ flexWrap: "wrap" }}>
          <button type="button" style={leise} disabled={laedt} onClick={onAbbrechen}>
            Abbrechen
          </button>
          <button type="button" style={knopf} disabled={laedt} onClick={onVerwerfen}>
            Verwerfen und neu laden
          </button>
          {phase.art === "konflikte" ? (
            <button ref={ersterRef} type="button" style={primaer} onClick={onUebernehmen}>
              Übernehmen und speichern
            </button>
          ) : (
            <button ref={ersterRef} type="button" style={primaer} disabled={laedt} onClick={onAnwenden}>
              {laedt ? "Lädt …" : "Meine Änderungen auf den neuen Stand anwenden"}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
